import { EventEmitter, Injectable } from '@angular/core';
import { Configuration } from '../models/configuration.model';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { PriceInformation } from '../models/price-information';
import { PriceSettings } from '../settings/price-settings';
import { FloorTypes } from '../enums/floorTypes.enum';
import { ApiService } from './api.service';
import { ErrorService } from './error.service';
import { ErrorTypes } from '../enums/errorTypes.enum';
import { IWall } from '../interfaces/wall.interface';
import { WallFinish } from '../models/wallFinish.model';
import { WallSide } from '../enums/wallSide.enum';
import { FinishTypes } from '../enums/finishTypes.enum';
import { DoorType } from '../enums/doorType.enum';
import { DoorPrice } from '../models/doorPrice.model';
import { DoorPosition } from '../models/doorPosition.model';
import { SiliconeColours } from '../enums/siliconeColours.enum';
import { Purpose } from '../enums/purpose';
import { InnerWall } from '../models/innerWall.model';
import { InnerWallType } from '../enums/innerWallType.enum';

@Injectable({
  providedIn: 'root',
})
export class PriceService {
  priceInformation$: BehaviorSubject<PriceInformation> = new BehaviorSubject<PriceInformation>({
    loading: true,
    price: 12345.5,
  });
  pricesLoaded$: EventEmitter<any> = new EventEmitter<any>();
  priceEnabled = false;
  pricesAreBeingLoaded = false;
  doorPricesCalculated = '';
  doorPrices = 0;
  prices: Record<string, number> = null;
  doorPriceCache: Record<string, DoorPrice> = {};

  constructor(
    private apiService: ApiService,
    private errorService: ErrorService
  ) {}

  get hasPrices(): boolean {
    return this.prices !== null;
  }

  async updatePrice(configuration: Configuration): Promise<number> {
    if (!this.priceEnabled) {
      return;
    }
    this.priceLoading();
    if (this.prices === null) {
      if (this.pricesAreBeingLoaded) {
        this.priceLoading();
        return;
      }
      try {
        this.prices = await this.apiService.get<Record<string, number>>('api/prices').toPromise();
      } catch (error) {
        this.errorService.throwError(ErrorTypes.InvalidAxToken);
        this.pricesAreBeingLoaded = false;
        return;
      }
      this.pricesAreBeingLoaded = false;
      this.pricesLoaded$.emit();
    }

    let price = this.calculateTotalPriceExcludingDoors(configuration);
    if (price === false) {
      this.returnInvalidPrice();
      return;
    }
    if (this.generateIdentifierForDoors(configuration) === this.doorPricesCalculated) {
      price += this.doorPrices;
    } else {
      const doorPrices = await this.getPricesOfDoors(configuration);
      if (doorPrices === false) {
        this.returnInvalidPrice();
        return;
      }

      price += doorPrices;
      this.doorPrices = doorPrices;
      this.doorPricesCalculated = this.generateIdentifierForDoors(configuration);
    }
    this.priceInformation$.next({ loading: false, price });

    return price;
  }

  generateIdentifierForDoors(configuration: Configuration): any {
    let text = '';
    configuration.settings.doorSettings.forEach(door => {
      text += this.getIDForDoorPrice(
        door,
        configuration.settings.wallThickness,
        configuration.settings.purpose !== Purpose.Cooling
      );
    });
    text += '|' + configuration.settings.wallThickness;
    return text;
  }

  priceLoading() {
    this.priceInformation$.next({ loading: true, price: 0 });
  }

  calculateTotalPriceExcludingDoors(configuration: Configuration): number | false {
    let price = 0;
    const basePrice = this.calculateModularBasicPrice(configuration);
    if (basePrice === false) {
      return false;
    }
    price += basePrice;

    const additionalCostOfFloor = this.calculateAdditionalCostsOfFloor(configuration);
    if (additionalCostOfFloor === false) {
      return false;
    }
    price += additionalCostOfFloor;

    const costOfVentilationProfiles = this.calculateCostOfVentilationProfiles(configuration);
    if (costOfVentilationProfiles === false) {
      return false;
    }
    price += costOfVentilationProfiles;

    const costOfPlinths = this.calculateCostOfPlinths(configuration);
    if (costOfPlinths === false) {
      return false;
    }
    price += costOfPlinths;

    const costOfFinishes = this.calculateCostOfWallFinishes(configuration);
    if (costOfFinishes === false) {
      return false;
    }
    price += costOfFinishes;

    const costOfValve = this.calculateCostOfAirPressureEqualizerValve(configuration);
    if (costOfValve === false) {
      return false;
    }
    price += costOfValve;

    const costOfUProfiles = this.calculatePriceOfUProfiles(configuration);
    if (costOfUProfiles === false) {
      return false;
    }
    price += costOfUProfiles;

    configuration.settings.innerWalls.forEach(iw => {
      const costOfInnerWall = this.calculatePriceOfInnerWall(iw, configuration);
      if (costOfInnerWall === false) {
        return false;
      }
      price += costOfInnerWall;
    });

    return price;
  }

  calculateCostOfAirPressureEqualizerValve(configuration: Configuration): number | false {
    if (configuration.settings.purpose === Purpose.Cooling) {
      return 0;
    }
    const articleItemId = PriceSettings.airPressureEqualizerValve;

    if (!this.prices[articleItemId]) {
      return false;
    }

    return this.prices[articleItemId] * this.calculateNumberOfAirPressureEqualizerValves(configuration);
  }

  calculateNumberOfAirPressureEqualizerValves(configuration: Configuration): number {
    return 1 + configuration.settings.innerWalls.length;
  }

  calculateModularBasicPrice(configuration: Configuration): number | false {
    let price = 0;
    const costOfPoles = this.calculateCostOfCornerBeams(configuration);
    if (costOfPoles === false) {
      return false;
    }
    price += costOfPoles;

    const costOfWalls = this.calculateCostOfWalls(configuration);
    if (costOfWalls === false) {
      return false;
    }
    price += costOfWalls;

    const costOfStandardFloor = this.calculateCostOfStandardFloor(configuration);
    if (costOfStandardFloor === false) {
      return false;
    }
    price += costOfStandardFloor;

    const costOfSilicone = this.calculateCostsOfAllSilicone(configuration);
    if (costOfSilicone === false) {
      return false;
    }
    price += costOfSilicone;

    return price;
  }

  returnInvalidPrice() {
    this.priceInformation$.next({ loading: false, price: 0, invalid: true });
  }

  calculateCostOfWalls(configuration: Configuration): number | false {
    let totalCost = 0;

    for (let index = 0; index < 4; index++) {
      const wall: IWall = this.getWallByWallSide(index, configuration);
      const wallPrice = this.calculateCostOfWall(wall, configuration);
      if (wallPrice === false) {
        return false;
      }
      totalCost += +wallPrice;
    }

    const ceilingCost = this.calculateCostOfCeiling(configuration);
    if (ceilingCost === false) {
      return false;
    }
    totalCost += +ceilingCost;

    return totalCost;
  }

  calculateCostOfWallFinishes(configuration: Configuration): number | false {
    let totalCost = 0;

    for (let index = 0; index < 4; index++) {
      const wall: IWall = this.getWallByWallSide(index, configuration);
      const wallFinish = configuration.settings.wallFinishes[index];

      const insidePrice = this.calculateCostOfWallInsideFinish(wall, wallFinish);
      const outsidePrice = this.calculateCostOfWallOutsideFinish(wall, wallFinish);
      if (insidePrice === false || outsidePrice === false) {
        return false;
      }
      totalCost += insidePrice + outsidePrice;
    }

    const ceilingInsidePrice = this.calculateCostOfCeilingInsideFinish(
      configuration,
      configuration.settings.wallFinishes[WallSide.ceiling]
    );
    const ceilingOutsidePrice = this.calculateCostOfCeilingOutsideFinish(
      configuration,
      configuration.settings.wallFinishes[WallSide.ceiling]
    );
    if (ceilingInsidePrice === false || ceilingOutsidePrice === false) {
      return false;
    }
    totalCost += ceilingInsidePrice + ceilingOutsidePrice;
    return totalCost;
  }

  getWallByWallSide(index: number, configuration: Configuration) {
    const wall: IWall = {
      side: index as WallSide,
      height: Math.max(configuration.settings.heightInMM / 1000, 2.1),
      depth: configuration.settings.lengthInMM / 1000,
      width: configuration.settings.widthInMM / 1000,
      wallLength: 0,
      wallThickness: configuration.settings.wallThickness,
    };
    if (wall.side === WallSide.north || wall.side === WallSide.south) {
      wall.wallLength = configuration.settings.widthInMM / 1000;
    } else {
      wall.wallLength = configuration.settings.lengthInMM / 1000;
    }
    return wall;
  }

  calculateCostOfCeiling(configuration: Configuration) {
    const ceilingSurface = (configuration.settings.lengthInMM / 1000) * (configuration.settings.widthInMM / 1000);

    const wallThicknessPrice =
      this.prices[PriceSettings.wallCoresToArticleNumbers[configuration.settings.wallThickness]];
    if (wallThicknessPrice === undefined) {
      return false;
    }
    const coreCost = wallThicknessPrice * ceilingSurface;
    return coreCost;
  }

  calculateCostOfCeilingInsideFinish(configuration: Configuration, finish: WallFinish) {
    const ceilingSurface = (configuration.settings.lengthInMM / 1000) * (configuration.settings.widthInMM / 1000);

    const interiorPrice = this.prices[PriceSettings.wallFinishToArticleNumbers[finish.interior]];
    if (interiorPrice === undefined) {
      return false;
    }
    const finishCost = interiorPrice * ceilingSurface;

    return finishCost;
  }

  calculateCostOfCeilingOutsideFinish(configuration: Configuration, finish: WallFinish) {
    const ceilingSurface = (configuration.settings.lengthInMM / 1000) * (configuration.settings.widthInMM / 1000);

    const exteriorPrice = this.prices[PriceSettings.wallFinishToArticleNumbers[finish.exterior]];
    if (exteriorPrice === undefined) {
      return false;
    }
    const finishCost = exteriorPrice * ceilingSurface;

    return finishCost;
  }

  calculateCostOfWall(wall: IWall, configuration: Configuration): number | false {
    const squareMetersOfWall = this.getWallSurfaceArea(wall);

    const wallThicknessPrice =
      this.prices[PriceSettings.wallCoresToArticleNumbers[configuration.settings.wallThickness]];

    if (wallThicknessPrice === undefined) {
      return false;
    }

    const coreCost = wallThicknessPrice * squareMetersOfWall;
    return coreCost;
  }

  calculateCostOfWallInsideFinish(wall: IWall, finish: WallFinish): number | false {
    const squareMetersOfWall = this.getWallSurfaceArea(wall);

    const interiorPrice = this.prices[PriceSettings.wallFinishToArticleNumbers[finish.interior]];
    if (interiorPrice === undefined) {
      return false;
    }
    const finishCost = interiorPrice * squareMetersOfWall;
    return finishCost;
  }

  calculateCostOfWallOutsideFinish(wall: IWall, finish: WallFinish): number | false {
    const squareMetersOfWall = this.getWallSurfaceArea(wall);
    const exteriorPrice = this.prices[PriceSettings.wallFinishToArticleNumbers[finish.exterior]];
    if (exteriorPrice === undefined) {
      return false;
    }
    const finishCost = exteriorPrice * squareMetersOfWall;

    return finishCost;
  }

  getWallSurfaceArea(wall: IWall) {
    return wall.height * wall.wallLength;
  }

  calculateCostOfWallFinishesForAllFinishes(configuration: Configuration): Record<FinishTypes, number | false> {
    const result: Record<FinishTypes, number | false> = {
      [FinishTypes.STEEL]: this.calculateCostOfWallFinishesByFinish(FinishTypes.STEEL, configuration),
      [FinishTypes.STEEL_PET55]: this.calculateCostOfWallFinishesByFinish(FinishTypes.STEEL_PET55, configuration),
      [FinishTypes.RVS304_LINE_BRUSH]: this.calculateCostOfWallFinishesByFinish(
        FinishTypes.RVS304_LINE_BRUSH,
        configuration
      ),
      [FinishTypes.RVS304_COLLED_ROLLED]: this.calculateCostOfWallFinishesByFinish(
        FinishTypes.RVS304_COLLED_ROLLED,
        configuration
      ),
      [FinishTypes.RVS304_DESIGN_5WL]: this.calculateCostOfWallFinishesByFinish(
        FinishTypes.RVS304_DESIGN_5WL,
        configuration
      ),
      [FinishTypes.RVS304_LINEN]: this.calculateCostOfWallFinishesByFinish(FinishTypes.RVS304_LINEN, configuration),
      [FinishTypes.RVS304_ROUND_BRUSH]: this.calculateCostOfWallFinishesByFinish(
        FinishTypes.RVS304_ROUND_BRUSH,
        configuration
      ),
      [FinishTypes.RVS316_ROUND_BRUSH]: this.calculateCostOfWallFinishesByFinish(
        FinishTypes.RVS316_ROUND_BRUSH,
        configuration
      ),
    };
    return result;
  }

  calculateCostOfWallFinishesByFinish(finishTypeToFilter: FinishTypes, configuration: Configuration) {
    let totalCost = 0;

    for (let index = 0; index < 4; index++) {
      const wall: IWall = this.getWallByWallSide(index, configuration);
      const wallFinish: WallFinish = configuration.settings.wallFinishes[index];
      if (wallFinish.interior === finishTypeToFilter) {
        const priceInside = this.calculateCostOfWallInsideFinish(wall, wallFinish);
        if (!priceInside) {
          return false;
        }
        totalCost += priceInside;
      }
      if (wallFinish.exterior === finishTypeToFilter) {
        const priceOutside = this.calculateCostOfWallOutsideFinish(wall, wallFinish);
        if (!priceOutside) {
          return false;
        }
        totalCost += priceOutside;
      }
    }
    const ceilingFinish: WallFinish = configuration.settings.wallFinishes[WallSide.ceiling];
    if (ceilingFinish.interior === finishTypeToFilter) {
      const priceInside = this.calculateCostOfCeilingInsideFinish(configuration, ceilingFinish);
      if (!priceInside) {
        return false;
      }
      totalCost += priceInside;
    }
    if (ceilingFinish.exterior === finishTypeToFilter) {
      const priceInside = this.calculateCostOfCeilingOutsideFinish(configuration, ceilingFinish);
      if (!priceInside) {
        return false;
      }
      totalCost += priceInside;
    }
    return totalCost;
  }

  calculateCostOfCornerBeams(configuration: Configuration): number | false {
    const verticalCornersLengthInM = this.calculateVerticalCornerBeams(configuration);
    const horizontalCornersLengthInM = this.calculateHorizontalCornerBeams(configuration);

    const articleItemId = PriceSettings.cornerBeamThicknessToArticles[configuration.settings.wallThickness];

    if (this.prices && this.prices[articleItemId]) {
      const price = (verticalCornersLengthInM + horizontalCornersLengthInM) * this.prices[articleItemId];
      return price;
    }
    console.log('Price for ' + articleItemId + ' not found.');
    return false;
  }

  calculateHorizontalCornerBeams(configuration: Configuration) {
    let lengthInMM = 0;
    lengthInMM += this.calculateInsidePerimeter(configuration);
    if (configuration.settings.floorType !== FloorTypes.NONE) {
      lengthInMM += this.calculateInsidePerimeter(configuration);
    }
    return lengthInMM / 1000;
  }

  calculateVerticalCornerBeams(configuration: Configuration) {
    const panelThickness = configuration.settings.wallThickness;
    let lengthInMM = 0;

    if (configuration.settings.floorType !== FloorTypes.NONE) {
      lengthInMM += this.height(configuration) + 2 * panelThickness;
    } else {
      lengthInMM += this.height(configuration) + panelThickness;
    }
    lengthInMM *= 4;
    return lengthInMM / 1000;
  }

  calculateCostOfStandardFloor(configuration: Configuration): number | false {
    const floorSurfaceInSqM = this.floorSurface(configuration);

    const internalFinishArticleItemId = PriceSettings.floorTypesToArticleNumbers[FloorTypes.DURAFLOOR];
    const externalFinishArticleItemId = PriceSettings.wallFinishToArticleNumbers[FinishTypes.STEEL];
    const coreArticleItemId = PriceSettings.wallCoresToArticleNumbers[configuration.settings.wallThickness];

    if (
      this.prices[internalFinishArticleItemId] &&
      this.prices[externalFinishArticleItemId] &&
      this.prices[coreArticleItemId]
    ) {
      const pricePerSqM =
        this.prices[internalFinishArticleItemId] +
        this.prices[coreArticleItemId] +
        this.prices[externalFinishArticleItemId];
      const price = floorSurfaceInSqM * pricePerSqM;
      return price;
    }
    console.log(
      'Price for ' +
        internalFinishArticleItemId +
        ' or ' +
        externalFinishArticleItemId +
        ' or ' +
        coreArticleItemId +
        ' not found.'
    );
    return false;
  }

  calculateAdditionalCostsOfFloor(configuration: Configuration): number | false {
    if (configuration.settings.floorType === FloorTypes.NONE) {
      return -this.calculateCostOfStandardFloor(configuration);
    }
    let price = 0;
    if (configuration.settings.floorType !== FloorTypes.DURAFLOOR) {
      const floorSurfaceInSqM = this.floorSurface(configuration);
      const internalFinishArticleItemId = PriceSettings.floorTypesToArticleNumbers[configuration.settings.floorType];
      if (!this.prices[internalFinishArticleItemId]) {
        return false;
      }
      price += floorSurfaceInSqM * this.prices[internalFinishArticleItemId];
    }

    if (configuration.settings.wallFinishes[+WallSide.floor].exterior !== FinishTypes.STEEL) {
      const floorSurfaceInSqM = this.floorSurface(configuration);
      const externalFinishArticleItemId =
        PriceSettings.wallFinishToArticleNumbers[configuration.settings.wallFinishes[+WallSide.floor].exterior];
      if (!this.prices[externalFinishArticleItemId]) {
        return false;
      }
      price += floorSurfaceInSqM * this.prices[externalFinishArticleItemId];
    }
    return price;
  }

  calculateCostOfVentilationProfiles(configuration: Configuration): number | false {
    if (configuration.settings.floorType === FloorTypes.NONE || configuration.settings.ventilationProfiles === false) {
      return 0;
    }

    const longSide = Math.max(configuration.settings.widthInMM, configuration.settings.lengthInMM);
    const shortSide = Math.min(configuration.settings.widthInMM, configuration.settings.lengthInMM);
    const wallThickness = configuration.settings.wallThickness;

    const totalLength = ((Math.ceil(longSide / 400) + 3) * (shortSide + wallThickness * 2)) / 1000;

    const ventilationProfileArticleItemId = PriceSettings.ventilationProfiles;
    if (!this.prices[ventilationProfileArticleItemId]) {
      return false;
    }
    const price = this.prices[ventilationProfileArticleItemId] * totalLength;
    return price;
  }

  calculateCostOfPlinths(configuration: Configuration): number | false {
    if (configuration.settings.plinthsInside === false && configuration.settings.plinthsOutside === false) {
      return 0;
    }
    const factorForOutside = 8;
    const totalDoorLength = configuration.settings.doorSettings.reduce(
      (sum, current) => sum + current.doorModel.panelWidth,
      0
    );
    let insideLengthInMM = this.calculateInsidePerimeter(configuration) - totalDoorLength;
    insideLengthInMM += configuration.settings.innerWalls
      .map(iw =>
        iw.placedOnSide === WallSide.east || iw.placedOnSide === WallSide.west
          ? configuration.settings.widthInMM
          : configuration.settings.lengthInMM
      )
      .reduce((sum, current) => (sum += 2 * current), 0);

    const outsideLengthInMM =
      this.calculateInsidePerimeter(configuration) +
      configuration.settings.wallThickness * factorForOutside -
      totalDoorLength;

    let lengthInM = 0;
    if (configuration.settings.plinthsInside) {
      lengthInM += insideLengthInMM / 1000;
    }
    if (configuration.settings.plinthsOutside) {
      lengthInM += outsideLengthInMM / 1000;
    }
    lengthInM = Math.ceil(lengthInM / 4) * 4;
    const quantity = lengthInM / 4;
    const plinthsArticleItemId = PriceSettings.plinths;
    if (!this.prices[plinthsArticleItemId]) {
      return false;
    }
    let price = this.prices[plinthsArticleItemId] * quantity;

    const plinthsScrewsArticleItemId = PriceSettings.plinthsScrews;
    if (!this.prices[plinthsScrewsArticleItemId]) {
      return false;
    }
    price += (this.prices[plinthsScrewsArticleItemId] * lengthInM) / 0.6;
    return price;
  }

  async getPricesOfDoors(configuration: Configuration): Promise<number | false> {
    let totalPrice = 0;
    for (const door of configuration.settings.doorSettings) {
      const price = await this.getPriceOfDoor(
        door,
        configuration.settings.wallThickness,
        configuration.settings.purpose !== Purpose.Cooling
      );
      if (price === false) {
        this.returnInvalidPrice();
        return;
      }
      totalPrice += price.Price;
    }

    return totalPrice;
  }

  getIDForDoorPrice(door: DoorPosition, wallThickness: number, isFreezer: boolean) {
    return (
      door.doorModel.openingHeight +
      '-' +
      door.doorModel.openingWidth +
      '|' +
      wallThickness +
      (isFreezer ? 'F' : 'C') +
      door.leafExteriorFinish +
      door.leafInteriorFinish +
      door.doorModel.type +
      '|' +
      door.doorHandleType +
      '|' +
      door.hingeType
    );
  }

  async getPriceOfDoor(door: DoorPosition, wallThickness: number, isFreezer: boolean): Promise<DoorPrice | false> {
    let type = '';

    const id = this.getIDForDoorPrice(door, wallThickness, isFreezer);
    if (Object.keys(this.doorPriceCache).includes(id)) {
      return this.doorPriceCache[id];
    }
    switch (door.doorModel.type) {
      case DoorType.Turn:
        type = 'DE';
        break;
      case DoorType.Slide:
        type = 'EN';
        break;
    }
    switch (wallThickness) {
      case 60:
        type += '06';
        break;
      case 80:
        type += '08';
        break;
      case 100:
        type += '10';
        break;
      case 140:
        type += '14';
        break;
      case 180:
        type += '18';
        break;
    }
    try {
      const price = await firstValueFrom(
        this.apiService.getDoorPrice(
          type,
          door.doorModel.openingHeight,
          door.doorModel.openingWidth,
          isFreezer,
          door.leafInteriorFinish,
          door.leafExteriorFinish,
          door.doorHandleType,
          door.hingeType
        )
      );
      this.doorPriceCache[id] = price;
      return price;
    } catch {
      this.errorService.throwError(ErrorTypes.InvalidAxToken);
      return false;
    }
  }

  getSiliconeColourForFinish(finishType: FinishTypes | FloorTypes): SiliconeColours {
    switch (finishType) {
      case FinishTypes.STEEL:
      case FloorTypes.DURAFLOOR:
        return SiliconeColours.White;

      case FinishTypes.STEEL_PET55:
      case FinishTypes.RVS304_ROUND_BRUSH:
      case FinishTypes.RVS316_ROUND_BRUSH:
      case FinishTypes.RVS304_COLLED_ROLLED:
      case FinishTypes.RVS304_LINE_BRUSH:
      case FinishTypes.RVS304_LINEN:
      case FinishTypes.RVS304_DESIGN_5WL:
      case FloorTypes.ALUMINUM25:
      case FloorTypes.ALUMINUM35:
      case FloorTypes.STEELSMOOTH3:
      case FloorTypes.STEELANTISLIP5:
        return SiliconeColours.Transparent;

      case FloorTypes.ANTISLIP:
      case FloorTypes.REINFORCEDANTISLIP:
      case FloorTypes.RVSANTISLIPR13:
        return SiliconeColours.Bronze;
    }
  }

  calculateCostsOfAllSilicone(configuration: Configuration): number | false {
    const priceBronze = this.calculateCostOfSilicone(configuration, SiliconeColours.Bronze);
    const priceTransparent = this.calculateCostOfSilicone(configuration, SiliconeColours.Transparent);
    const priceWhite = this.calculateCostOfSilicone(configuration, SiliconeColours.White);
    if (priceBronze === false || priceTransparent === false || priceWhite === false) {
      return false;
    }
    return priceBronze + priceTransparent + priceWhite;
  }

  calculateCostOfSilicone(configuration: Configuration, siliconeColour: SiliconeColours): number | false {
    const quantity = Math.ceil(this.getQuantityOfSiliconeByColour(configuration, siliconeColour));
    const price = this.prices[PriceSettings.siliconeToArticleNumbers[siliconeColour]];
    if (!price) {
      return false;
    }
    return price * quantity;
  }

  getQuantityOfSiliconeByColour(configuration: Configuration, colour: SiliconeColours): number {
    const widthInM = configuration.settings.widthInMM / 1000;
    const lengthInM = configuration.settings.lengthInMM / 1000;

    let quantity = 0;
    if (configuration.settings.floorType === FloorTypes.NONE) {
      if (this.getSiliconeColourForFinish(configuration.settings.wallFinishes[WallSide.south].interior) === colour) {
        quantity += (widthInM * 2) / 10;
      }
      if (this.getSiliconeColourForFinish(configuration.settings.wallFinishes[WallSide.north].interior) === colour) {
        quantity += (widthInM * 2) / 10;
      }
      if (this.getSiliconeColourForFinish(configuration.settings.wallFinishes[WallSide.east].interior) === colour) {
        quantity += (lengthInM * 2) / 10;
      }
      if (this.getSiliconeColourForFinish(configuration.settings.wallFinishes[WallSide.west].interior) === colour) {
        quantity += (lengthInM * 2) / 10;
      }
      configuration.settings.innerWalls.forEach(iw => {
        if (this.getSiliconeColourForFinish(iw.interiorFinish) === colour) {
          quantity += iw.placedOnSide % 2 === 0 ? widthInM : (lengthInM * 2) / 10;
        }
      });
    } else {
      if (this.getSiliconeColourForFinish(configuration.settings.floorType) === colour) {
        quantity += Math.ceil(widthInM / 1.2) * lengthInM;
        quantity += Math.ceil(lengthInM / 2.4) * widthInM;
        quantity /= 5;
        quantity = Math.ceil(quantity);
      }
    }
    return quantity;
  }

  calculatePriceOfUProfiles(configuration: Configuration): number | false {
    if (configuration.settings.floorType !== FloorTypes.NONE) {
      return 0;
    }
    if (configuration.settings.wallThickness <= 100) {
      return this.calculatePriceOfUProfilesUpTo10(configuration);
    }
    return false;
  }

  calculatePriceOfUProfilesUpTo10(configuration: Configuration): number | false {
    let price = 0;
    const wallThicknessInMM = configuration.settings.wallThickness;
    const pvcProfileArticleId = PriceSettings.uProfiles[wallThicknessInMM];
    if (!pvcProfileArticleId) {
      return false;
    }
    const lengthInMM = configuration.settings.lengthInMM;
    const widthInMM = configuration.settings.widthInMM;
    let quantity = Math.ceil(((lengthInMM + widthInMM + 2 * wallThicknessInMM) * 2) / 3 / 1000);
    configuration.settings.innerWalls
      .map(iw =>
        iw.placedOnSide === WallSide.east || iw.placedOnSide === WallSide.west
          ? configuration.settings.widthInMM
          : configuration.settings.lengthInMM
      )
      .forEach(value => (quantity += Math.ceil(value / 1000 / 3)));
    quantity = Math.ceil(quantity);
    if (this.prices[pvcProfileArticleId] === undefined) {
      return false;
    }
    price += 3 * quantity * this.prices[pvcProfileArticleId];
    const pvcProfileCornerArticleId = PriceSettings.uProfilesCorners[wallThicknessInMM];

    if (!pvcProfileCornerArticleId || this.prices[pvcProfileCornerArticleId] === undefined) {
      return false;
    }
    price += 4 * this.prices[pvcProfileCornerArticleId];

    if (this.prices[PriceSettings.butyl] === undefined) {
      return false;
    }
    price += quantity * this.prices[PriceSettings.butyl];

    if (this.prices[PriceSettings.strokePlug] === undefined) {
      return false;
    }
    price += quantity * 18 * this.prices[PriceSettings.strokePlug];

    if (this.prices[PriceSettings.washer] === undefined) {
      return false;
    }
    price += quantity * 18 * this.prices[PriceSettings.washer];
    return price;
  }

  calculatePriceOfInnerWall(iw: InnerWall, configuration: Configuration): number | false {
    let price = 0;
    let surface = 0;
    let innerWallLength = 0;
    const innerWallHeight = configuration.settings.heightInMM / 1000;
    if (iw.placedOnSide === WallSide.east || iw.placedOnSide === WallSide.west) {
      surface = (configuration.settings.widthInMM / 1000) * (configuration.settings.heightInMM / 1000);
      innerWallLength = configuration.settings.widthInMM / 1000;
    } else {
      surface = (configuration.settings.lengthInMM / 1000) * (configuration.settings.heightInMM / 1000);
      innerWallLength = configuration.settings.lengthInMM / 1000;
    }
    const wallThicknessPrice =
      this.prices[PriceSettings.wallCoresToArticleNumbers[configuration.settings.wallThickness]];
    if (wallThicknessPrice === undefined) {
      return false;
    }

    price += wallThicknessPrice * surface;

    const interiorPrice = this.prices[PriceSettings.wallFinishToArticleNumbers[iw.interiorFinish]];
    if (interiorPrice === undefined) {
      return false;
    }

    price += interiorPrice * surface;

    const exteriorPrice = this.prices[PriceSettings.wallFinishToArticleNumbers[iw.exteriorFinish]];
    if (exteriorPrice === undefined) {
      return false;
    }

    price += exteriorPrice * surface;
    switch (iw.type) {
      case InnerWallType.threeway: {
        let amount = (innerWallLength + innerWallHeight) * 2 + (4 * configuration.settings.wallThickness) / 1000;
        if (configuration.settings.floorType === FloorTypes.NONE) {
          amount -= innerWallLength + (2 * configuration.settings.wallThickness) / 1000;
        }
        const threeWayCornerItemId = PriceSettings.threeWayCornerByThickness[configuration.settings.wallThickness];
        if (this.prices[threeWayCornerItemId] === undefined) {
          return false;
        }
        price += amount * this.prices[threeWayCornerItemId];
        break;
      }
      case InnerWallType.lProfile: {
        const amount = innerWallLength * 2 + innerWallHeight * 4;
        const lProfileItemId = PriceSettings.lProfileByThickness[configuration.settings.wallThickness];
        if (this.prices[lProfileItemId] === undefined) {
          return false;
        }
        price += amount * this.prices[lProfileItemId];
        break;
      }
    }
    return price;
  }

  private height(configuration: Configuration) {
    return Math.max(configuration.settings.heightInMM, 2100);
  }

  private floorSurface(configuration: Configuration) {
    return (configuration.settings.lengthInMM / 1000) * (configuration.settings.widthInMM / 1000);
  }

  private calculateInsidePerimeter(configuration: Configuration): number {
    return 2 * configuration.settings.lengthInMM + 2 * configuration.settings.widthInMM;
  }
}
